<!--
  SignupFlow — root orchestrator component, replaces OnboardingChatbotV1.vue. Holds
  useSignupFlow(); renders the chat shell (header + progress + transcript) while
  phase === 'chat', and swaps to the full-screen SuccessScreen once the account is
  created. Owns the chat-area template ref for auto-scroll.
-->
<template>
  <div class="app-shell">
    <div v-if="flow.phase.value === 'chat'" class="screen chat-screen">
      <div class="chat-header">
        <div class="header-avatar">
          <img :src="flow.activeChar.value.avatar" :alt="flow.activeChar.value.name" />
        </div>
        <div class="header-text">
          <div class="header-name">{{ flow.activeChar.value.name }} · {{ brandName }}</div>
          <div class="header-status">En ligne</div>
        </div>
      </div>

      <ProgressBar
        :visible="flow.showProgress.value"
        :current="flow.stepIdx.value"
        :total="flow.totalSteps.value"
        :show-total="flow.totalKnown.value"
        :pct="flow.progressPct.value"
      />

      <div class="sr-only" aria-live="polite" role="status">{{ flow.stepAnnouncement.value }}</div>

      <div ref="chatAreaRef" class="chat-area" role="log" aria-label="Conversation d'inscription">
        <ChatTranscript
          :messages="flow.messages.value"
          :avatar-src="flow.activeChar.value.avatar"
          :character-name="flow.activeChar.value.name"
          :submit-in-flight="flow.submitInFlight.value"
          :email-error="flow.emailError.value"
          @edit="flow.goBackToStep"
          @select="onSelect"
          @care-type="flow.selectCareType"
          @confirm-address="flow.confirmAddress"
          @longform-submit="flow.onLongFormSubmit"
          @auth-google="flow.chooseGoogle"
          @auth-email="flow.chooseEmail"
          @already-have-account="flow.alreadyHaveAccount"
          @email-submit="flow.submitEmail"
          @accept-cgua="flow.acceptCgua"
        />
      </div>
    </div>

    <SuccessScreen
      v-else
      :config="flow.successConfig.value"
      :character-src="flow.activeChar.value.success"
      :character-name="flow.activeChar.value.name"
      @primary="flow.onSuccessPrimary"
      @secondary="flow.onSuccessSecondary"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted, provide } from 'vue'
import { useSignupFlow } from '../composables/useSignupFlow'
import { ProgressBar, prefersReducedMotion } from '@twg/ui'
import { ADDRESS_CONFIG_KEY } from '../composables/useAddressAutocomplete'
import ChatTranscript from './ChatTranscript.vue'
import SuccessScreen from './SuccessScreen.vue'

// `locationiqKey` (the public map key — client-exposed by design) + `countryCodes`
// (brand.searchable_country_codes) come from #signup-root data-* via the island entry.
const props = withDefaults(
  defineProps<{
    brand?: string
    locale?: string
    brandName?: string
    locationiqKey?: string
    countryCodes?: string
  }>(),
  {
    brand: 'nounou-top',
    locale: 'fr',
    brandName: 'Nounou-Top',
    locationiqKey: '',
    countryCodes: 'FR',
  },
)

// Address autocomplete config for AddressStep (provide/inject — no globals).
provide(ADDRESS_CONFIG_KEY, {
  apiKey: props.locationiqKey,
  countryCodes: props.countryCodes,
  locale: props.locale,
})

const flow = useSignupFlow()
const chatAreaRef = ref<HTMLElement | null>(null)

// Route a choice-card selection to the right handler by the card's step.
function onSelect(stepKey: string, key: string): void {
  if (stepKey === 'mn-subprofile') flow.selectSubProfile(key)
  else if (stepKey === 'universe') flow.selectUniverse(key)
  else flow.selectRole(key) // mp-role | mn-role
}

// Auto-scroll to the newest message as the timeline grows.
watch(
  () => flow.messages.value.length,
  () => {
    void nextTick(() => {
      const el = chatAreaRef.value
      if (!el) return
      el.scrollTo({ top: el.scrollHeight, behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
    })
  },
)

onMounted(() => flow.start())
</script>

<style scoped>
.app-shell {
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background: var(--white);
  width: 100%;
}
@media (min-width: 640px) {
  .app-shell {
    max-width: 440px;
    margin: 0 auto;
    border-radius: 28px;
    overflow: hidden;
    box-shadow: 0 16px 48px rgba(27, 65, 89, 0.18);
    min-height: 720px;
    max-height: calc(100vh - 80px);
    max-height: calc(100dvh - 80px);
  }
}
@media (min-width: 1280px) {
  .app-shell {
    max-width: 480px;
    border-radius: 32px;
    box-shadow: 0 20px 60px rgba(27, 65, 89, 0.2);
  }
}
.screen {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  position: relative;
}
.chat-screen {
  height: 100%;
  overflow: hidden;
}
.chat-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e9f0; /* off-palette — DS pass to arbitrate */
  background: var(--white);
  flex-shrink: 0;
}
.header-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #e0eaf5;
}
.header-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.header-text {
  display: flex;
  flex-direction: column;
}
.header-name {
  font-weight: 600;
  font-size: 15px;
  color: #1a2733;
}
.header-status {
  font-size: 13px;
  color: #16a34a; /* off-palette "online" green — DS pass to arbitrate */
}
.header-status::before {
  content: '•';
  margin-right: 4px;
}
.chat-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 0;
  -webkit-overflow-scrolling: touch;
}
.chat-area::-webkit-scrollbar {
  display: none;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
